/**
 * crewBaselineCheck.js
 *
 * Individual baseline validation for WBC3 28.1.4 / 28.1.5
 * Every crew member MUST have:
 * - Medical Fitness (ML5 or ENG1) - VALID (not expired)
 * - Sea Survival (PST or RYA Basic Sea Survival) - VALID (not expired)
 *
 * FT-158: Handles both expiry_date (snake_case) and expiryDate (camelCase)
 *
 * FleetSkipper - Built for Mariners.
 */

const MEDICAL_CERTS = [
  'ML5', 'ML5 Medical Certificate',
  'ENG1', 'ENG1 Medical Certificate',
  'Medical Fitness Certificate',
  'Medical Fitness'
];

const SEA_SURVIVAL_CERTS = [
  'STCW Personal Survival Techniques', 'STCW PST', 'PST',
  'Personal Survival Techniques',
  'RYA Basic Sea Survival', 'Basic Sea Survival',
  'Sea Survival Training', 'Sea Survival'
];

/**
 * Check if a certificate is expired
 * FT-158 FIX: Handle both expiry_date (snake_case) and expiryDate (camelCase)
 */
export const isCertExpired = (cert) => {
  const expiryValue = cert.expiry_date || cert.expiryDate;
  if (!expiryValue) return false;  // No expiry = perpetual
  const expiry = new Date(expiryValue);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return expiry < today;
};

/**
 * Check if crew member has VALID (non-expired) certificate of specified types
 */
const hasValidCertType = (crew, certTypes) => {
  const certs = crew.certificates || [];
  return certs.some(cert => {
    const certName = cert?.type || cert?.title || '';
    const matchesType = certTypes.some(type =>
      certName.toLowerCase().includes(type.toLowerCase())
    );
    return matchesType && !isCertExpired(cert);
  });
};

/**
 * Check all crew members for required baseline certs (Medical + Sea Survival)
 * Returns failures array - empty if all pass
 *
 * @param {Array} selectedCrewMembers - Full crew objects (with certificates array)
 * @returns {Object} { valid: boolean, failures: Array }
 */
export function checkCrewBaselines(selectedCrewMembers) {
  const failures = [];

  selectedCrewMembers.forEach(crew => {
    const missing = [];

    // Check Medical Fitness (VALID)
    if (!hasValidCertType(crew, MEDICAL_CERTS)) {
      missing.push('Medical Fitness (ML5/ENG1)');
    }

    // Check Sea Survival (VALID)
    if (!hasValidCertType(crew, SEA_SURVIVAL_CERTS)) {
      missing.push('Sea Survival (PST)');
    }

    if (missing.length > 0) {
      failures.push({
        crewId: crew.id,
        crewName: crew.full_name || crew.name || 'Unknown',
        missing
      });
    }
  });

  return {
    valid: failures.length === 0,
    failures
  };
}

export default checkCrewBaselines;
